if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      /* Seventeen Mirrors lightbox rewrite (2 Sep 2026). The stock modal
         stacked every image at natural size: opening felt zoomed right in,
         there was no next/previous, and on touch the only exit was the X.
         Now: one image at a time fitted to the screen, tap the image to
         zoom in and out, arrows + swipe + arrow keys to move, and Esc /
         backdrop tap / X to close. Order and visibility follow the
         gallery's colour filter (data-sm-hide from sm-product.js), so the
         lightbox never shows another colour's photos. Styles: section 17
         of assets/sm-product.css. */
      constructor() {
        super();
        this.dialog = this.querySelector('[role="dialog"]');
        this.content = this.querySelector('.product-media-modal__content');
        this.prevBtn = this.querySelector('[data-sm-lb-prev]');
        this.nextBtn = this.querySelector('[data-sm-lb-next]');
        this.counter = this.querySelector('[data-sm-lb-counter]');
        this.items = [];
        this.index = 0;

        if (this.prevBtn) this.prevBtn.addEventListener('click', (e) => { e.stopPropagation(); this.step(-1); });
        if (this.nextBtn) this.nextBtn.addEventListener('click', (e) => { e.stopPropagation(); this.step(1); });

        this.addEventListener('keyup', (event) => {
          if (event.code === 'ArrowRight') this.step(1);
          if (event.code === 'ArrowLeft') this.step(-1);
        });

        /* One pointer handler owns taps and swipes, and it stops
           propagation so ModalDialog's media-modal listener (any mouse
           click closes) never fires. Closing is decided here (backdrop
           tap, mouse or touch) or by the X and Esc, which the base class
           still handles. */
        if (this.dialog) {
          this.dialog.addEventListener('pointerdown', (e) => {
            this.pStart = { x: e.clientX, y: e.clientY };
          });
          this.dialog.addEventListener('pointerup', (e) => {
            e.stopPropagation();
            const s = this.pStart;
            this.pStart = null;
            if (!s) return;
            const dx = e.clientX - s.x;
            const dy = e.clientY - s.y;
            const zoomed = this.classList.contains('sm-zoomed');
            if (!zoomed && Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.5) {
              this.step(dx < 0 ? 1 : -1);
              return;
            }
            if (Math.abs(dx) > 8 || Math.abs(dy) > 8) return; /* a drag or a pan, not a tap */
            if (e.target.closest('button')) return; /* buttons handle their own clicks */
            const active = this.activeItem();
            if (active && active.tagName === 'IMG' && e.target === active) {
              this.toggleZoom(e);
              return;
            }
            if (!e.target.closest('deferred-media, product-model')) this.hide();
          });
        }
      }

      show(opener) {
        super.show(opener);
        this.buildItems();
        const openerId = (opener && opener.getAttribute('data-media-id')) || '';
        const suffix = openerId.split('-').pop();
        const found = this.items.findIndex((el) => el.getAttribute('data-media-id') === suffix);
        this.index = found >= 0 ? found : 0;
        this.render();
      }

      hide() {
        this.resetZoom();
        this.syncGallery();
        super.hide();
      }

      gallery() {
        return (
          document.getElementById(`MediaGallery-${this.dataset.section}`) || document.querySelector('media-gallery')
        );
      }

      allMedia() {
        return this.content ? Array.from(this.content.querySelectorAll(':scope > [data-media-id]')) : [];
      }

      buildItems() {
        const all = this.allMedia();
        const byId = {};
        all.forEach((el) => (byId[el.getAttribute('data-media-id')] = el));
        const ordered = [];
        const gal = this.gallery();
        if (gal) {
          gal.querySelectorAll('.product__media-list .product__media-item:not([data-sm-hide])').forEach((li) => {
            const id = (li.dataset.mediaId || '').split('-').pop();
            if (byId[id]) ordered.push(byId[id]);
          });
        }
        this.items = ordered.length ? ordered : all;
        const single = this.items.length < 2;
        if (this.prevBtn) this.prevBtn.hidden = single;
        if (this.nextBtn) this.nextBtn.hidden = single;
        if (this.counter) this.counter.hidden = single;
      }

      activeItem() {
        return this.items[this.index] || null;
      }

      step(dir) {
        if (this.items.length < 2) return;
        this.resetZoom();
        this.index = (this.index + dir + this.items.length) % this.items.length;
        this.render();
      }

      render() {
        this.allMedia().forEach((el) => el.classList.remove('active'));
        const active = this.activeItem();
        if (!active) return;
        active.classList.add('active');
        if (this.counter) this.counter.textContent = `${this.index + 1} / ${this.items.length}`;
        if (active.nodeName === 'DEFERRED-MEDIA') {
          const tpl = active.querySelector('template');
          if (tpl && tpl.content && tpl.content.querySelector('.js-youtube')) active.loadContent();
        }
      }

      toggleZoom(event) {
        const img = this.activeItem();
        if (!img || img.tagName !== 'IMG' || !this.content) return;
        if (this.classList.contains('sm-zoomed')) {
          this.resetZoom();
          return;
        }
        const rect = img.getBoundingClientRect();
        const fx = rect.width ? (event.clientX - rect.left) / rect.width : 0.5;
        const fy = rect.height ? (event.clientY - rect.top) / rect.height : 0.5;
        const natural = img.naturalWidth || rect.width * 2;
        const zoomW = Math.min(natural, Math.max(rect.width * 2.2, this.content.clientWidth));
        if (zoomW <= rect.width + 20) return; /* nothing meaningful to zoom into */
        this.classList.add('sm-zoomed');
        img.style.width = `${zoomW}px`;
        const zoomH = zoomW * (rect.height / rect.width);
        this.content.scrollLeft = fx * zoomW - this.content.clientWidth / 2;
        this.content.scrollTop = fy * zoomH - this.content.clientHeight / 2;
      }

      resetZoom() {
        this.classList.remove('sm-zoomed');
        this.querySelectorAll('img[style]').forEach((img) => (img.style.width = ''));
        if (this.content) {
          this.content.scrollLeft = 0;
          this.content.scrollTop = 0;
        }
      }

      /* On close, land the page gallery on the image the customer was
         looking at, so the flow reads as one continuous browse. */
      syncGallery() {
        const gal = this.gallery();
        const active = this.activeItem();
        if (!gal || !active || typeof gal.setActiveMedia !== 'function') return;
        const id = active.getAttribute('data-media-id');
        const li = gal.querySelector(`.product__media-item[data-media-id$="-${id}"]`);
        if (li) gal.setActiveMedia(li.dataset.mediaId, false);
      }
    }
  );
}
